import type { NextRouter } from 'next/router';

import * as yup from 'yup';

import { zoomLinkValidator } from '@lib/zoom';

import type { runningContextStringType } from '@lib/zoom/types';

import {
  ZOOMAPP_CTX_PARAM
} from '@lib/zoom/constants';

// Mozilla/5.0 ZoomWebKit/537.36 (KHTML, like Gecko) ZoomApps/1.0
export const _isZoomApp = (userAgentString: string | null | undefined) => userAgentString?.match(/zoom/i);

export const isZoomApp = () => {
  if (typeof window !== 'undefined') {
    return _isZoomApp(window.navigator.userAgent);
  }
  return false;
};

export const zoomAppBasePath = process.env.NEXT_PUBLIC_ZM_BASE_PATH;

export const redirectToZoomApp = (router: NextRouter) => {
  if (isZoomApp() && zoomAppBasePath) {
    router.push(zoomAppBasePath);
  }
};

export const isMeetingContext = (context: runningContextStringType) => {
  if (context?.match(/^(inMeeting|inWebinar|meeting|webinar)$/i)) {
    return true;
  };
  return false;
};

export const getResourceType = (context: runningContextStringType) => {
  if (context?.match(/inMeeting|meeting/i)) {
    return 'meetings';
  }
  if (context?.match(/inWebinar|webinar/i)) {
    return 'webinars';
  }
};

export const getErrorMessage = (error: any) => {
  let errorMessage;
  if (error?.reason) {
    errorMessage = error.reason;
  } else {
    errorMessage = 'Something went wrong. Please try again.';
  }
  return errorMessage;
};

export const getWebAppLink = (type = 'login', context: string, headerContext: any) => {
  const baseUrl = `${process.env.NEXT_PUBLIC_URL}${type === 'login' ? 'login' : 'signup'}`;
  const queryParams = {
    utm_source: 'zoomapp',
    utm_medium: 'app',
    [ZOOMAPP_CTX_PARAM]: context,
    alias: headerContext?.uid
  };

  const queryString = new URLSearchParams(queryParams).toString();
  return `${baseUrl}?${queryString}`;
};

export const sandboxLink = process.env.NEXT_PUBLIC_SANDBOX_URL;

export const isHostRole = (role = '') => role.match(/^(host)$/i);

export const isHostOrCoHostRole = (role = '') => role.match(/^(host|co-host|coHost)$/i);

export const meetingLinkSchema = yup.object().shape({
  meeting_link: zoomLinkValidator()
});

export const isWindows = (userAgent: string) => userAgent?.match(/windows/i);

/**
 * Hashes a string (e.g. Zoom user ID) into a Firestore-safe document ID
 * using SHA-256. Zoom user IDs can contain characters like `/`, `=`, `+`
 * which are invalid in Firestore document IDs.
 */
export async function hashForFirestore(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
